import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Stack,
  Typography,
  Card,
  Grid,
  Button,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Wifi,
  Phone,
  Tv,
  Zap,
  GraduationCap,
  Search,
  ChevronLeft,
  CheckCircle2,
  Sliders,
  Eye,
  RefreshCw,
  Plus,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Page from '../components/Page';
import { getServices, getServiceProviders, convertProductToV2, convertServicesBatchToV2 } from '../api/services';
import { BRAND_IMAGES } from '../utils/assets';

// Helper to detect brand logo
const getBrandLogo = (category, details) => {
  const text = `${category || ''} ${details?.name || ''} ${details?.planName || ''}`
    .toLowerCase()
    .replace(/[\s\-_]/g, '');
  const matchedKey = Object.keys(BRAND_IMAGES).find((key) => text.includes(key));
  return matchedKey ? BRAND_IMAGES[matchedKey] : null;
};

// Category Definitions
const CATEGORIES_CONFIG = {
  data: {
    key: 'Data',
    title: 'Data bundles',
    icon: Wifi,
    color: '#0066FE',
    bg: '#EFF6FF',
    description: 'SME, Corporate and Direct Data plans',
    defaultBadgeText: (product) => product.category,
    productTitle: (product) => product.details?.name || `${product.category} Plan`,
    defaultValidity: '30 Days',
  },
  airtime: {
    key: 'Airtime',
    title: 'Airtime VTU and share',
    icon: Phone,
    color: '#16A34A',
    bg: '#DCFCE7',
    description: 'Virtual Top-Up and Share airtime',
    defaultBadgeText: (product) => `${product.category} Airtime`,
    productTitle: (product) => `${product.category} Airtime VTU`,
    defaultValidity: 'Instant VTU',
  },
  cable: {
    key: 'Cable',
    title: 'Cable TV',
    icon: Tv,
    color: '#D97706',
    bg: '#FEF3C7',
    description: 'DSTV, GOTV, Startimes and ShowMax package activations',
    defaultBadgeText: (product) => product.category,
    productTitle: (product) => product.details?.name || `${product.category} Package`,
    defaultValidity: 'Monthly',
  },
  electricity: {
    key: 'Electricity',
    title: 'Electricity bills',
    icon: Zap,
    color: '#EA580C',
    bg: '#FFEDD5',
    description: 'Prepaid and postpaid utility token purchases across DISCO distribution hubs',
    defaultBadgeText: (product) => product.category || 'Electricity',
    productTitle: (product) => product.category || product.details?.name || 'Electricity Token',
    defaultValidity: 'Instant Token',
  },
  resultcheck: {
    key: 'ResultCheck',
    title: 'Result checkers',
    icon: GraduationCap,
    color: '#7C3AED',
    bg: '#EDE9FE',
    description: 'WAEC, NECO and NABTEB examination scratch cards and e-PINs',
    defaultBadgeText: (product) => product.category || 'Result Checker',
    productTitle: (product) => `${product.category} Scratch Card & e-PIN`,
    defaultValidity: 'Instant e-PIN',
  },
};

export default function ServiceCategoryPage() {
  const { categoryKey, category } = useParams();
  const navigate = useNavigate();

  const activeCategoryParam = (categoryKey || category || 'data').toLowerCase();
  const currentConfig = CATEGORIES_CONFIG[activeCategoryParam] || CATEGORIES_CONFIG.data;

  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters (default v2 so legacy products are filtered out from V2 by default)
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [v2Filter, setV2Filter] = useState('v2'); // 'v2' | 'legacy' | 'all'
  const [searchQuery, setSearchQuery] = useState('');
  const [convertingId, setConvertingId] = useState(null);
  const [convertingBatch, setConvertingBatch] = useState(false);

  // Fetch all services
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [srvRes, provRes] = await Promise.all([getServices(), getServiceProviders()]);
      if (srvRes.ok && srvRes.data?.data) {
        setServices(srvRes.data.data);
      }
      if (provRes.ok && provRes.data?.data) {
        setProviders(provRes.data.data);
      }
    } catch (err) {
      toast.error('Failed to load services data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // All services under this category (e.g. Data)
  const categoryServices = useMemo(
    () =>
      services.filter(
        (s) => (s.service || '').toLowerCase() === currentConfig.key.toLowerCase()
      ),
    [services, currentConfig.key]
  );

  // Extract unique Categories for this service
  const availableCategories = useMemo(() => {
    const set = new Set();
    categoryServices.forEach((s) => {
      if (s.category) set.add(s.category.trim());
    });
    return ['All', ...Array.from(set)];
  }, [categoryServices]);

  // Filtered products list
  const filteredProducts = useMemo(
    () =>
      categoryServices.filter((s) => {
        const matchesCategory =
          selectedCategory === 'All' ||
          (s.category || '').trim() === selectedCategory;

        const matchesV2 =
          v2Filter === 'all' ||
          (v2Filter === 'v2' && s.useV2Engine) ||
          (v2Filter === 'legacy' && !s.useV2Engine);

        const q = searchQuery.toLowerCase().trim();
        const matchesSearch =
          !q ||
          (s.details?.name || '').toLowerCase().includes(q) ||
          (s.category || '').toLowerCase().includes(q) ||
          String(s.id).includes(q) ||
          String(s.amount || '').includes(q) ||
          String(s.details?.amount || '').includes(q);

        return matchesCategory && matchesV2 && matchesSearch;
      }),
    [categoryServices, selectedCategory, v2Filter, searchQuery]
  );

  // Stats for this category
  const totalCount = categoryServices.length;
  const v2Count = useMemo(() => categoryServices.filter((s) => s.useV2Engine).length, [categoryServices]);
  const legacyProducts = useMemo(() => categoryServices.filter((s) => !s.useV2Engine), [categoryServices]);
  const legacyCount = legacyProducts.length;
  const activeProvidersCount = useMemo(() => providers.filter((p) => p.isActive).length, [providers]);

  const handleConvertProductToV2 = async (product) => {
    setConvertingId(product.id);
    try {
      const res = await convertProductToV2(product.id, product, {
        candidateProviders: providers,
      });
      if (res.ok) {
        if (res.isVerified && res.matchedPlan) {
          const provName = res.matchedPlan.processorSlug?.toUpperCase() || res.providerSlug?.toUpperCase();
          toast.success(
            `✅ Matched on ${provName}: ${res.matchedPlan.planName || ''} (Plan ID: ${res.matchedPlan.planId}, Cost: ₦${res.matchedPlan.costPrice}) upgraded to V2!`
          );
        } else {
          toast.success(`${product.category} ${product.details?.name || ''} upgraded to V2! (Plan ID: ${res.planId})`);
        }
        fetchData();
      } else {
        toast.error(res.data?.message || 'Failed to convert product to V2');
      }
    } catch (err) {
      toast.error('Error converting product to V2');
    } finally {
      setConvertingId(null);
    }
  };

  const handleConvertAllLegacyToV2 = async () => {
    if (legacyCount === 0) return;
    setConvertingBatch(true);
    try {
      const { successCount, total, verifiedCount, customCount } = await convertServicesBatchToV2(
        legacyProducts,
        { candidateProviders: providers }
      );
      if (verifiedCount > 0) {
        toast.success(
          `⚡ Converted ${successCount} of ${total} products to V2! (${verifiedCount} verified with provider catalog, ${customCount} custom)`
        );
      } else {
        toast.success(`Successfully converted ${successCount} of ${total} products to V2!`);
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to convert products to V2');
    } finally {
      setConvertingBatch(false);
    }
  };

  const IconComp = currentConfig.icon;
  const isAirtime = currentConfig.key.toLowerCase() === 'airtime';
  const isElectricity = currentConfig.key.toLowerCase() === 'electricity';

  const renderPricingDetails = (pricing) => {
    const { discountRate, buyingRate, netMargin, retailPrice, buyingPrice, margin } = pricing;

    if (isAirtime) {
      return (
        <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: '16px', mb: 2.5, border: '1px solid #F3F4F6' }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Retail Discount
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0066FE' }}>
                {discountRate.toFixed(2)}%
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Vendor Profit Rate
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#16A34A' }}>
                {buyingRate.toFixed(2)}%
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ pt: 1, borderTop: '1px dashed #E5E7EB', mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: netMargin >= 0 ? '#16A34A' : '#DC2626',
                  fontWeight: 700,
                }}
              >
                Net Commission: {netMargin >= 0 ? `+${netMargin.toFixed(2)}%` : `${netMargin.toFixed(2)}%`}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      );
    }

    if (isElectricity) {
      return (
        <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: '16px', mb: 2.5, border: '1px solid #F3F4F6' }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Billing Mode
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#EA580C' }}>
                Prepaid & Postpaid
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Service Fee
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
                {retailPrice > 0 ? `₦${retailPrice.toLocaleString()}` : 'Free (₦0)'}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ pt: 1, borderTop: '1px dashed #E5E7EB', mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>
                Flexible Token Recharge: Customer enters meter top-up amount
              </Typography>
            </Grid>
          </Grid>
        </Box>
      );
    }

    return (
      <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: '16px', mb: 2.5, border: '1px solid #F3F4F6' }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              Selling Price
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0066FE' }}>
              ₦{retailPrice.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              Buying Price
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
              ₦{buyingPrice.toLocaleString()}
            </Typography>
          </Grid>
          {margin > 0 && (
            <Grid item xs={12} sx={{ pt: 1, borderTop: '1px dashed #E5E7EB', mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#16A34A', fontWeight: 700 }}>
                Est. Margin: +₦{margin.toLocaleString()}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <Page title={`${currentConfig.title} | Dashboard`}>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Navigation & Header */}
        <Box mb={3.5}>
          <Button
            onClick={() => navigate('/dashboard/services')}
            startIcon={<ChevronLeft size={16} />}
            sx={{
              color: '#0066FE',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.925rem',
              p: 0,
              mb: 2,
              background: 'none',
              '&:hover': {
                background: 'none',
                textDecoration: 'underline',
              },
            }}
          >
            Categories
          </Button>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            gap={2}
          >
            <Stack direction="row" alignItems="center" spacing={2.5}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  bgcolor: currentConfig.bg,
                  color: currentConfig.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <IconComp size={28} />
              </Box>

              <div>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: '#111827',
                    letterSpacing: '-0.02em',
                    fontSize: { xs: '1.5rem', sm: '1.85rem' },
                    lineHeight: 1.2,
                    mb: 0.25,
                  }}
                >
                  {currentConfig.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
                  {currentConfig.description}
                </Typography>
              </div>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                onClick={fetchData}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={14} />}
                sx={{
                  borderRadius: '24px',
                  bgcolor: '#EAECEF',
                  color: '#1F2937',
                  px: 2.25,
                  py: 0.85,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#DFE2E6',
                    boxShadow: 'none',
                  },
                }}
              >
                Refresh
              </Button>
              <Button
                onClick={() => navigate(`/dashboard/services/create?service=${currentConfig.key.toLowerCase()}`)}
                startIcon={<Plus size={15} />}
                sx={{
                  borderRadius: '24px',
                  bgcolor: '#0066FE',
                  color: '#FFFFFF',
                  px: 2.5,
                  py: 0.85,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#0052CC',
                    boxShadow: 'none',
                  },
                }}
              >
                Add product
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Quick Summary Pill Bar */}
        <Stack direction="row" spacing={1.5} mb={3.5} flexWrap="wrap" useFlexGap>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.65,
              borderRadius: '20px',
              bgcolor: '#FFFFFF',
              border: '1px solid #ECEEF1',
              color: '#111827',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
          >
            <span>Total:</span>
            <span style={{ color: '#0066FE' }}>{totalCount} products</span>
          </Box>

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.65,
              borderRadius: '20px',
              bgcolor: '#FFFFFF',
              border: '1px solid #ECEEF1',
              color: '#111827',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
          >
            <span>V2 Multi-Processor:</span>
            <span style={{ color: '#16A34A' }}>{v2Count} active</span>
          </Box>

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.65,
              borderRadius: '20px',
              bgcolor: '#FFFFFF',
              border: '1px solid #ECEEF1',
              color: '#111827',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
          >
            <span>Vendors connected:</span>
            <span>{activeProvidersCount} active</span>
          </Box>
        </Stack>

        {/* Filter Card */}
        <Card
          sx={{
            p: 3,
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECEEF1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            mb: 4,
          }}
        >
          {availableCategories.length > 1 && (
            <Box mb={2.5}>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: '#6B7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1.25,
                }}
              >
                Categories:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {availableCategories.map((cat) => {
                  const isSelected = selectedCategory === cat;
                  return (
                    <Button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      sx={{
                        borderRadius: '20px',
                        px: 2.25,
                        py: 0.65,
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: 'none',
                        bgcolor: isSelected ? '#111827' : '#F3F4F6',
                        color: isSelected ? '#FFFFFF' : '#374151',
                        border: isSelected ? 'none' : '1px solid #E5E7EB',
                        '&:hover': {
                          bgcolor: isSelected ? '#1F2937' : '#E5E7EB',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {cat === 'All' ? 'All categories' : cat}
                    </Button>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* Search Bar & V2 Routing Toggle */}
          <Grid
            container
            spacing={2}
            alignItems="center"
            pt={availableCategories.length > 1 ? 2 : 0}
            sx={{ borderTop: availableCategories.length > 1 ? '1px dashed #F1F3F5' : 'none' }}
          >
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                size="small"
                placeholder={`Search ${currentConfig.title.toLowerCase()} by name, code or price...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} color="#9CA3AF" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '24px',
                    bgcolor: '#F9FAFB',
                    fontSize: '0.875rem',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': { borderColor: '#0066FE' },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} alignItems="center" gap={1}>
                <Typography sx={{ fontSize: '0.8rem', color: '#6B7280', fontWeight: 500 }}>
                  Routing:
                </Typography>
                {[
                  { id: 'v2', label: 'V2 active' },
                  { id: 'legacy', label: legacyCount > 0 ? `Legacy (${legacyCount})` : 'Legacy' },
                  { id: 'all', label: 'All' },
                ].map((t) => {
                  const active = v2Filter === t.id;
                  return (
                    <Button
                      key={t.id}
                      onClick={() => setV2Filter(t.id)}
                      size="small"
                      sx={{
                        borderRadius: '16px',
                        px: 1.5,
                        py: 0.35,
                        fontSize: '0.775rem',
                        fontWeight: active ? 600 : 500,
                        textTransform: 'none',
                        bgcolor: active ? '#0066FE' : '#F3F4F6',
                        color: active ? '#FFFFFF' : '#4B5563',
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: active ? '#0052CC' : '#E5E7EB',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {t.label}
                    </Button>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* Products Grid Content */}
        {loading && (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress size={36} />
          </Box>
        )}

        {!loading && filteredProducts.length === 0 && (
          <Card
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: '24px',
              bgcolor: '#FFFFFF',
              border: '1px solid #ECEEF1',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            }}
          >
            {v2Filter === 'v2' && legacyCount > 0 ? (
              <>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '20px',
                    bgcolor: '#FEF3C7',
                    color: '#D97706',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <Zap size={28} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', mb: 1 }}>
                  No active V2 products yet in {currentConfig.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', maxWidth: 480, mx: 'auto', mb: 3 }}>
                  You have <strong>{legacyCount}</strong> legacy {currentConfig.title.toLowerCase()} product{legacyCount === 1 ? '' : 's'} running on single-processor routing. Upgrade them to V2 to enable automated multi-processor failover and route redundancy.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                  <Button
                    variant="contained"
                    onClick={handleConvertAllLegacyToV2}
                    disabled={convertingBatch}
                    sx={{
                      borderRadius: '24px',
                      bgcolor: '#16A34A',
                      color: '#FFFFFF',
                      px: 3.5,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      '&:hover': { bgcolor: '#15803D' },
                    }}
                  >
                    {convertingBatch ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      `⚡ Convert All (${legacyCount}) to V2`
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setV2Filter('legacy')}
                    sx={{
                      borderRadius: '24px',
                      borderColor: '#D1D5DB',
                      color: '#374151',
                      px: 2.5,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      '&:hover': { bgcolor: '#F9FAFB' },
                    }}
                  >
                    View Legacy Products ({legacyCount})
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => navigate(`/dashboard/services/create?service=${currentConfig.key.toLowerCase()}`)}
                    sx={{
                      borderRadius: '24px',
                      color: '#0066FE',
                      px: 2,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                    }}
                  >
                    + Create New V2 Product
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                  No products found
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', maxWidth: 460, mx: 'auto', mb: 3 }}>
                  No products matched your current filters{selectedCategory !== 'All' ? ` for category (${selectedCategory})` : ''}. Try clearing your filters or search keyword.
                </Typography>
                <Button
                  onClick={() => {
                    setSelectedCategory('All');
                    setSearchQuery('');
                    setV2Filter('all');
                  }}
                  sx={{
                    borderRadius: '24px',
                    bgcolor: '#0066FE',
                    color: '#FFFFFF',
                    px: 3,
                    py: 0.85,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&:hover': { bgcolor: '#0052CC' },
                  }}
                >
                  Reset filters
                </Button>
              </>
            )}
          </Card>
        )}

        {!loading && filteredProducts.length > 0 && (
          <div>
            {legacyCount > 0 && v2Filter === 'v2' && (
              <Card
                sx={{
                  p: 2.25,
                  mb: 3,
                  borderRadius: '20px',
                  bgcolor: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: '#FEF3C7',
                      color: '#D97706',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Zap size={18} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#92400E' }}>
                      {legacyCount} legacy {currentConfig.title.toLowerCase()} product{legacyCount === 1 ? '' : 's'} not yet on V2
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#B45309' }}>
                      Upgrade remaining products to unlock failover redundancy and multiple fallback processors.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleConvertAllLegacyToV2}
                    disabled={convertingBatch}
                    sx={{
                      borderRadius: '16px',
                      bgcolor: '#16A34A',
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      px: 2,
                      py: 0.6,
                      '&:hover': { bgcolor: '#15803D' },
                    }}
                  >
                    {convertingBatch ? <CircularProgress size={14} color="inherit" /> : `⚡ Convert All (${legacyCount}) to V2`}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setV2Filter('legacy')}
                    sx={{
                      borderRadius: '16px',
                      borderColor: '#D97706',
                      color: '#92400E',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      px: 1.75,
                      py: 0.6,
                      '&:hover': { bgcolor: '#FEF3C7' },
                    }}
                  >
                    View Legacy
                  </Button>
                </Stack>
              </Card>
            )}

            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
              <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500 }}>
                Showing <strong>{filteredProducts.length}</strong> of {totalCount} products
              </Typography>
              {v2Filter === 'legacy' && legacyCount > 0 && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleConvertAllLegacyToV2}
                  disabled={convertingBatch}
                  sx={{
                    borderRadius: '16px',
                    bgcolor: '#16A34A',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#15803D' },
                  }}
                >
                  {convertingBatch ? <CircularProgress size={14} color="inherit" /> : `⚡ Convert All Legacy (${legacyCount}) to V2`}
                </Button>
              )}
            </Stack>

            <Grid container spacing={2.5}>
              {filteredProducts.map((product) => {
                const routesCount = (product.processorRoutes || []).length;
                const brandLogo = getBrandLogo(product.category, product.details);

                const discountRate = Number(product.discount || 0);
                const buyingRate = Number(product.buyingAmount || 0);
                const netMargin = buyingRate - discountRate;

                const retailPrice = Number(product.details?.amount || product.amount || 0);
                const buyingPrice = Number(product.buyingAmount || 0);
                const margin = retailPrice - buyingPrice;

                return (
                  <Grid item xs={12} sm={6} md={4} key={product.id}>
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: '20px',
                        bgcolor: '#FFFFFF',
                        border: '1px solid #ECEEF1',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.06)',
                          borderColor: '#DFE2E6',
                        },
                      }}
                    >
                      <div>
                        {/* Top Badges (Category & V2 Status) */}
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1.25,
                              py: 0.4,
                              borderRadius: '14px',
                              bgcolor: '#EFF6FF',
                              color: '#2563EB',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {product.category || currentConfig.key}
                          </Box>

                          {product.useV2Engine ? (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                px: 1.5,
                                py: 0.4,
                                borderRadius: '16px',
                                bgcolor: '#DCFCE7',
                                color: '#16A34A',
                                fontSize: '0.775rem',
                                fontWeight: 600,
                              }}
                            >
                              <CheckCircle2 size={13} />
                              <span>V2 Active ({routesCount})</span>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.4,
                                borderRadius: '16px',
                                bgcolor: '#F3F4F6',
                                color: '#6B7280',
                                fontSize: '0.775rem',
                                fontWeight: 600,
                              }}
                            >
                              Legacy
                            </Box>
                          )}
                        </Stack>

                        {/* Brand Logo & Product Name */}
                        <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                          {brandLogo && (
                            <Box
                              component="img"
                              src={brandLogo}
                              alt={product.category}
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: '12px',
                                objectFit: 'cover',
                                flexShrink: 0,
                                border: '1px solid #ECEEF1',
                              }}
                            />
                          )}
                          <div>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.025rem', color: '#111827', lineHeight: 1.2 }}>
                              {currentConfig.productTitle
                                ? currentConfig.productTitle(product)
                                : product.details?.name || `${product.category} Plan`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6B7280' }}>
                              #{product.id} • {product.validity || currentConfig.defaultValidity || 'Standard'}
                            </Typography>
                          </div>
                        </Stack>

                        {/* Pricing / Margin Details Box */}
                        {renderPricingDetails({
                          discountRate,
                          buyingRate,
                          netMargin,
                          retailPrice,
                          buyingPrice,
                          margin,
                        })}
                      </div>

                      {/* Card Action Buttons */}
                      <Stack direction="row" spacing={1.5} mt={1}>
                        {!product.useV2Engine ? (
                          <Button
                            fullWidth
                            onClick={() => handleConvertProductToV2(product)}
                            disabled={convertingId === product.id}
                            sx={{
                              borderRadius: '24px',
                              bgcolor: '#16A34A',
                              color: '#FFFFFF',
                              py: 0.85,
                              textTransform: 'none',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              boxShadow: 'none',
                              '&:hover': {
                                bgcolor: '#15803D',
                                boxShadow: 'none',
                              },
                            }}
                          >
                            {convertingId === product.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              '⚡ Convert to V2'
                            )}
                          </Button>
                        ) : (
                          <Button
                            fullWidth
                            onClick={() => navigate(`/dashboard/services/details/${product.id}`)}
                            startIcon={<Eye size={14} />}
                            sx={{
                              borderRadius: '24px',
                              bgcolor: '#EAECEF',
                              color: '#1F2937',
                              py: 0.85,
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              boxShadow: 'none',
                              '&:hover': {
                                bgcolor: '#DFE2E6',
                                boxShadow: 'none',
                              },
                            }}
                          >
                            View Details
                          </Button>
                        )}
                        <Button
                          fullWidth
                          onClick={() => navigate(`/dashboard/services/edit/${product.id}`)}
                          startIcon={<Sliders size={14} />}
                          sx={{
                            borderRadius: '24px',
                            bgcolor: '#0066FE',
                            color: '#FFFFFF',
                            py: 0.85,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            boxShadow: 'none',
                            '&:hover': {
                              bgcolor: '#0052CC',
                              boxShadow: 'none',
                            },
                          }}
                        >
                          Edit & Route
                        </Button>
                      </Stack>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </div>
        )}
      </Container>
    </Page>
  );
}
